<template>
  <div :class="wrapperStyles">
    <MapboxVue
      v-show="!hidden"
      :access-token="token"
      :map-options="mapOptions"
      :nav-control="navControl"
      :geolocate-control="geolocateControl"
      @map-init="handleInit"
      @map-load="handleLoad"
      @map-resize="handleResize"
      @map-dragend="handleDragEnd"
      @map-zoom="handleZoom()"
      @map-zoomend="handleZoomEnd"/>
    <slot name="map-filter"></slot>
    <slot name="extra"></slot>
    <SummaryTile
      v-if="this.getShowSummaryTile"
      :onHideSummaryTile="this.handleHideSummaryTile"
      :content="this.region.dangerDescription"
      :link="{ href: '#', title: `View full forecast for ${this.region.title}`, text: 'View full forecast' }"
      :onClick="handleTileClick(this.region.id)">

      <div v-if="this.region.title" slot="title">
        <h3 class="tile__heading tile__heading--title">{{ this.region.title }}</h3>
        <p>{{ $store.getters.regionDangerRating(this.selectedRegion).description }}</p>
      </div>
    </SummaryTile>
  </div>

</template>

<script>
  import bbox from '@turf/bbox';
  import constants from '../../constants';
  import MapboxVue from 'mapbox-gl-vue';
  import MapCloseControl from './MapCloseControl';
  import MapSizeControl from './MapSizeControl';
  import MapStyleControl from './MapStyleControl';
  import throttle from 'throttle-debounce/throttle';
  import SummaryTile from '../tiles/SummaryTile';
  import {mapState} from 'vuex';

  let mapbox = null;

  export default {
    components: {
      MapboxVue,
      SummaryTile,
    },

    props: {
      styles: {
        type: Object,
        default() {
          return {};
        },
      },

      options: {
        type: Object,
        default() {
          return {};
        },
      },

      hidden: {
        type: Boolean,
        default: false,
      },

      closable: {
        type: Boolean,
        default: true,
      },

      expandable: {
        type: Boolean,
        default: false,
      },

      expanded: {
        type: Boolean,
        default: false,
      },

      token: {
        type: String,
        default: 'pk.eyJ1IjoibXNjLWFkbWluIiwiYSI6ImNqZGRsZm9rNDAwY2Uyd29udTM5OWd1cmcifQ.Iwdb5AtkGpRVYxuUpOhlQg',
      },

      navControl: {
        type: Object,
        default() {
          return {
            show: true,
            position: 'bottom-right',
          };
        },
      },

      geolocateControl: {
        type: Object,
        default() {
          return {
            show: true,
            position: 'top-left',
            options: {
              positionOptions: {
                enableHighAccuracy: true,
              },
              trackUserLocation: true,
            },
          };
        },
      },

      regions: {
        type: Array,
        default() {
          return [];
        },
      },

      forecasts: {
        type: Array,
        default() {
          return [];
        },
      },

      observations: {
        type: Object,
      },

      clusterMaxZoom: {
        type: Number,
        default: 17.9,
      },

      clusterRadius: {
        type: Number,
        default: 50,
      },

      initialBounds: {
        type: Array,
        default() {
          return [
            [165.01859929057042, -47.54977421666661],
            [179.88384589789575, -34.00011395368569],
          ];
        },
      },

      selectedRegion: {
        type: Number,
      },

      onLoad: {
        type: Function,
      },

      onResize: {
        type: Function,
      },

      onDragEnd: {
        type: Function,
      },

      onZoomEnd: {
        type: Function,
      },

      onMobileToggle: {
        type: Function,
      },

      onSizeToggle: {
        type: Function,
      },

      onRegionSelect: {
        type: Function,
      },

      onTileClick: {
        type: Function,
      },

      onObservationSelect: {
        type: Function,
      },

      showSizeToggle: Boolean
    },

    watch: {
      observations() {
        if (mapbox.getSource('observations')) {
          mapbox.getSource('observations').setData(this.observations);
        }
      },

      showRecentPublicObs() {
        this.drawObservations();
      },
    },

    data() {
      return {
        style: constants.MAP_STYLES.TOPOGRAPHIC,
        showSummaryTile: true
      };
    },

    computed: {
      ...mapState({
        showRecentPublicObs: state => state.showRecentPublicObs,
      }),

      getShowSummaryTile() {
        return this.showSummaryTile && this.selectedRegion;
      },

      region() {
        return this.$store.getters.regionById(this.selectedRegion);
      },

      wrapperStyles() {
        const defaults = {
          'map-wrapper': true,
          'col-12': true,
        };

        const expandable = {
          'map-wrapper--expanded': this.expandable && this.expanded,
          'pr-0': this.expandable,
          'pl-md-0': this.expandable,
          'col-md-9': this.expandable && this.expanded,
          'col-lg-4': this.expandable && !this.expanded,
          'col-xl-10': this.expandable && this.expanded,
        };

        const controls = {
          'map-wrapper--summary-tile': this.getShowSummaryTile
        };

        return Object.assign({}, defaults, controls, expandable, this.styles);
      },

      mapOptions() {
        const defaults = {
          center: [174.9491338249743, -40.96312286412625],
          style: this.style,
          maxBounds: this.initialBounds,
        };

        return Object.assign({}, defaults, this.options);
      },
    },

    methods: {
      /**
       * Get an opacity value between 0 - 0.5 based on the current zoom level.
       */
      getZoomOpacity() {
        const zoom = Math.round(mapbox.getZoom());
        return Math.min(Math.max(-(zoom - 10), 0), 5) / 10;
      },

      getVisibleRegions() {
        const layers = this.regions.map((region) => `region-${region.id}-outline`);

        return mapbox
          .queryRenderedFeatures(undefined, {layers})
          .reduce((result, feature) => {
            if (!result.includes(feature.properties.id)) {
              result.push(feature.properties.id);
            }

            return result;
          }, []);
      },

      handleInit(map) {
        mapbox = map;

        if (this.onSizeToggle && this.showSizeToggle) {
          mapbox.addControl(new MapSizeControl(this.handleMapSizeToggle));
        }

        if (this.closable) {
          mapbox.addControl(new MapCloseControl(this.handleMapMobileToggle));
        }

        mapbox.addControl(new MapStyleControl(this.handleMapStyleToggle), 'top-left');
      },

      drawRegions() {
        this.regions.forEach((region) => {
          const dangerRating = this.$store.getters.regionDangerRating(region.id).rating;

          const regionId = 'region-'+region.id;
          const regionIdFill = 'region-'+region.id+'-fill';
          const regionIdOutline = 'region-'+region.id+'-outline';

          // Create a polygon for each region.
          mapbox.addLayer({
            id: regionIdFill,
            type: 'fill',
            source: regionId,
            paint: {
              'fill-color': constants.DANGER_COLORS[dangerRating],
              'fill-opacity': this.selectedRegion === region.id ? 0 : 0.5,
            },
          });
          // We have to create a seperate outline.
          // See https://github.com/mapbox/mapbox-gl-js/issues/3018
          mapbox.addLayer({
            id: regionIdOutline,
            type: 'line',
            source: regionId,
            paint: {'line-color': constants.DANGER_COLORS[dangerRating], 'line-width': 2},
          });

          // Hover state for regions.
          mapbox.on('mousemove', regionIdFill, (event) => {
            // The selected region has no hover state.
            if (this.selectedRegion === region.id) {
              return;
            }

            const opacity = this.getZoomOpacity();

            if (opacity > 0) {
              mapbox.setPaintProperty(regionIdFill, 'fill-opacity', opacity + 0.1);
            }
          });
          mapbox.on('mouseleave', regionIdFill, () => {
            // Don't reset the fill of the selected region (it has no fill).
            if (this.selectedRegion === region.id) {
              return;
            }

            mapbox.setPaintProperty(regionIdFill, 'fill-opacity', this.getZoomOpacity());
          });

          // Zoom to fit the the region when it's clicked.
          mapbox.on('click', regionIdFill, (event) => {
            if (this.selectedRegion === region.id) {
              return;
            }
            
            // If clicks are on observation point don't do anything
            const q = mapbox.queryRenderedFeatures(event.point, {
              'layers' : [
                'observation-recent-snowpack-unclustered',
                'observation-recent-avalanche-unclustered',
                'observation-old-snowpack-unclustered',
                'observation-old-avalanche-unclustered',
              ]
            });

            if (q.length > 0) {
              return;
            }

            // Selected regions have no fill.
            mapbox.setPaintProperty(regionIdFill, 'fill-opacity', 0);
            mapbox.fitBounds(bbox(event.features[0]), {padding: 20});

            this.handleRegionSelect(region);
          });
        });
      },

      async drawObservations() {
        if (!this.observations) {
          return;
        }

        const loadImages = () => {
          const loadImage = (layerName) => {
            return new Promise((resolve, reject) => {
              mapbox.loadImage(`${window.location.origin}/resources/themes/default/images/${layerName}-pin.png`, (error, publicObsPin) => {
                if (error) {
                  reject(error);
                }

                mapbox.addImage(`public-obs-${layerName}`, publicObsPin);
                resolve();
              });
            });
          };

          return new Promise((resolve, reject) => {
            Promise.all([
              loadImage('old-avalanche'),
              loadImage('old-snowpack'),
              loadImage('recent-avalanche'),
              loadImage('recent-snowpack'),
            ]).then(() => {
              resolve();
            }).catch(error => {
              reject(error);
            });
          });
        };

        const loadSelectedImages = () => {
          const loadImage = (layerName) => {
            mapbox.loadImage(
              `${window.location.origin}/resources/themes/default/images/${layerName}-pin-selected.png`,
              (error, publicObsSelectedPin) => {
                if (error) {
                  throw error;
                }
                mapbox.addImage(`public-obs-${layerName}-selected`, publicObsSelectedPin);
              }
            );
          };

          return new Promise((resolve, reject) => {
            Promise.all([
              loadImage('old-avalanche'),
              loadImage('old-snowpack'),
              loadImage('recent-avalanche'),
              loadImage('recent-snowpack'),
            ]).then(() => {
              resolve();
            }).catch(error => {
              reject(error);
            });
          });
        };

        const onSelectObs = (symbol) => {
          mapbox.flyTo({center: symbol.lngLat, zoom: 18});
          this.handleObservationSelect(symbol.features[0].properties.id);
        };

        // Load custom mapbox icons
        await loadImages();
        await loadSelectedImages();

        // Add cliusters
        mapbox.addLayer({
          id: 'observation-cluster',
          type: 'circle',
          source: 'observations',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': constants.BRAND_COLORS.SECONDARY,
            'circle-radius': 15,
            'circle-stroke-width': 5,
            'circle-stroke-color': constants.BRAND_COLORS.PRIMARY,
            'circle-stroke-opacity': 0.3,
          },
        });

        mapbox.addLayer({
          id: 'observation-cluster-count',
          type: 'symbol',
          source: 'observations',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 13,
          },
          paint: {
            'text-color': '#fff',
          },
        });

        // Unslected Obs
        mapbox.addLayer({
          id: 'observation-recent-avalanche-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Avalanche'],
            ['==', ['get', 'is_recent'], true],
            ['==', ['get', 'selected'], false],
          ],
          layout: {
            'icon-image': `public-obs-recent-avalanche`,
            'icon-size': 0.35,
          },
        });

        mapbox.addLayer({
          id: 'observation-old-avalanche-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Avalanche'],
            ['==', ['get', 'is_recent'], false],
            ['==', ['get', 'selected'], false],
          ],
          layout: {
            'icon-image': `public-obs-old-avalanche`,
            'icon-size': 0.35,
          },
        });

        mapbox.addLayer({
          id: 'observation-recent-snowpack-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Snowpack'],
            ['==', ['get', 'is_recent'], true],
            ['==', ['get', 'selected'], false],
          ],
          layout: {
            'icon-image': `public-obs-recent-snowpack`,
            'icon-size': 0.35,
          },
        });

        mapbox.addLayer({
          id: 'observation-old-snowpack-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Snowpack'],
            ['==', ['get', 'is_recent'], false],
            ['==', ['get', 'selected'], false],
          ],
          layout: {
            'icon-image': `public-obs-old-snowpack`,
            'icon-size': 0.35,
          },
        });

        // Bind unselected Obs event handler
        [
          'observation-recent-snowpack-unclustered',
          'observation-recent-avalanche-unclustered',
          'observation-old-snowpack-unclustered',
          'observation-old-avalanche-unclustered',
        ].forEach(layer => {
          mapbox.on('click', layer, (symbol) => {
            onSelectObs(symbol);
          });
        });

        // Selected Obs
        mapbox.addLayer({
          id: 'observation-selected-recent-avalanche-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Avalanche'],
            ['==', ['get', 'is_recent'], true],
            ['==', ['get', 'selected'], true],
          ],
          layout: {
            'icon-image': `public-obs-recent-avalanche-selected`,
            'icon-size': 0.35,
          },
        });

        mapbox.addLayer({
          id: 'observation-selected-old-avalanche-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Avalanche'],
            ['==', ['get', 'is_recent'], false],
            ['==', ['get', 'selected'], true],
          ],
          layout: {
            'icon-image': `public-obs-old-avalanche-selected`,
            'icon-size': 0.35,
          },
        });

        mapbox.addLayer({
          id: 'observation-selected-recent-snowpack-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Snowpack'],
            ['==', ['get', 'is_recent'], true],
            ['==', ['get', 'selected'], true],
          ],
          layout: {
            'icon-image': `public-obs-recent-snowpack-selected`,
            'icon-size': 0.35,
          },
        });

        mapbox.addLayer({
          id: 'observation-selected-old-snowpack-unclustered',
          type: 'symbol',
          source: 'observations',
          filter: [
            'all',
            ['!', ['has', 'point_count']],
            ['==', ['get', 'type'], 'Snowpack'],
            ['==', ['get', 'is_recent'], false],
            ['==', ['get', 'selected'], true],
          ],
          layout: {
            'icon-image': `public-obs-old-snowpack-selected`,
            'icon-size': 0.35,
          },
        });
      },

      addSources() {
        if (!mapbox.getSource('observations')) {
          mapbox.addSource('observations', {
            type: 'geojson',
            data: this.observations,
            cluster: true,
            clusterMaxZoom: this.clusterMaxZoom,
            clusterRadius: this.clusterRadius,
          });
        }

        this.regions.forEach((region) => {
          if (!region.geometry || mapbox.getSource(`region-${region.id}`) !== undefined) {
            return;
          }

          mapbox.addSource(`region-${region.id}`, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: region.geometry.layers[0].geometry,
              properties: {
                id: region.id,
                name: region.title,
              },
            },
          });
        });
      },

      handleLoad(map) {
        this.addSources();
        this.drawRegions();
        this.drawObservations();

        if (this.onLoad) {
          setTimeout(() => {
            this.onLoad(map);
          }, 300);
        }
      },

      handleZoom: throttle(200, function () {
        const opacity = this.getZoomOpacity();

        // Apply the opacity to each region polygon.
        this.regions.forEach((region) => {
          // Don't adjust the fill of the selected region.
          if (this.selectedRegion === region.id) {
            return;
          }

          mapbox.setPaintProperty(`region-${region.id}-fill`, 'fill-opacity', opacity);
        });
      }),

      handleResize() {
        if (this.onResize) {
          this.onResize(mapbox, this.getVisibleRegions());
        }
      },

      handleDragEnd() {
        if (this.onDragEnd) {
          this.onDragEnd(mapbox, this.getVisibleRegions());
        }
      },

      handleZoomEnd() {
        if (this.onZoomEnd) {
          this.onZoomEnd(mapbox, this.getVisibleRegions());
        }
      },

      handleMapMobileToggle(event, map) {
        if (this.onMobileToggle) {
          this.onMobileToggle(event, map);
        }
      },

      handleMapSizeToggle(event, map) {
        if (this.onSizeToggle) {
          this.onSizeToggle(event, map);
        }
      },

      handleHideSummaryTile() {
        this.showSummaryTile = false;
      },

      handleRegionSelect(region) {
        this.onRegionSelect(region);
        this.showSummaryTile = true;
      },

      handleTileClick(id) {
        return (event) => {
          this.onTileClick(event, id);
          this.showSummaryTile = false;
        }
      },

      handleObservationSelect(id) {
        this.onObservationSelect(id);
      },

      handleMapStyleToggle(event, map) {
        this.style = this.style === constants.MAP_STYLES.TOPOGRAPHIC
          ? constants.MAP_STYLES.SATELLITE
          : constants.MAP_STYLES.TOPOGRAPHIC;

        map.setStyle(this.style);

        // Redraw regions once the new map style has loaded.
        const interval = setInterval(() => {
          if (map.isStyleLoaded()) {
            this.addSources();
            this.drawRegions();
            this.drawObservations();
            clearInterval(interval);
          }
        }, 500);
      },
    },
  };
</script>
